import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { USER_SETTINGS } from 'cpool-constants';
import { AGGREGATE_NAMES, DEFAULT_USER_SETTINGS, USER_COMMANDS } from 'cpool-constants';
import type { SchemaUserResolver_Get } from 'cpool-tables/backend-resolvers';
import { diff, objectKeys } from 'cpool-utils';
import type { SubmitErrorHandler, SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { APICommunicator } from '~/api/APICommunicator';
import type { SchemaUserEditForm } from '~/features/userManagement/schemas/SchemaUserEditForm';
import { setQueryPosition } from '~/store/queryPositionStore';
import { queryKeys } from '~/utils/queryKeys';
import uploadFiles from '~/utils/uploadFiles.ts';

type UserSettingsPayload = USER_SETTINGS & {
    // nullable: the form schema turns an empty user/password pair into null
    smtp: SchemaUserEditForm['smtp'];
    certFile: SchemaUserEditForm['certFile'];
    certPassword: SchemaUserEditForm['certPassword'];
};

export default function useUserSettings(user: SchemaUserResolver_Get | undefined, close: () => void) {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    const uploadCert = async (file: File, password: string, userId = user?.id): Promise<number | null> => {
        const formData = new FormData();
        formData.append(file.name, file);
        const response: Response = await uploadFiles(
            `/advisers/certificateUpload?userId=${userId}&password=${encodeURIComponent(password)}`,
            {
                body: formData,
                credentials: 'include',
                method: 'post',
            },
        );

        if (!response.ok) throw new Error(t('TOP_HEADER.USER_SETTINGS.SETTINGS.ERROR_CHANGING_SETTINGS'));

        if (response.body && 'position' in response.body && typeof response.body.position === 'number')
            return response.body.position;

        return null;
    };

    const { mutate } = useMutation({
        mutationFn: async (data: UserSettingsPayload) => {
            let position;
            const smtp = data.smtp?.user && data.smtp.password ? data.smtp : null;

            if (data.certFile) {
                const uploadPosition = await uploadCert(data.certFile, data.certPassword ?? '');
                if (uploadPosition) position = uploadPosition;
            }

            if (diff(smtp, user?.smtp) !== undefined) {
                const { position: pos } = await APICommunicator.sendCommand({
                    aggregateId: user?.id,
                    aggregateName: AGGREGATE_NAMES.USER,
                    commandName: USER_COMMANDS.changeSMTP,
                    payload: {
                        smtp,
                    },
                });
                position = pos;
            }

            if (user) {
                const personalSettingsChanges: Partial<USER_SETTINGS> = Object.fromEntries(
                    objectKeys(DEFAULT_USER_SETTINGS)
                        .filter((key) => data[key] !== user.settings[key])
                        .map((key) => [key, data[key]] as const),
                );

                if (Object.keys(personalSettingsChanges).length > 0) {
                    const { position: pos } = await APICommunicator.sendCommand({
                        aggregateId: user.id,
                        aggregateName: AGGREGATE_NAMES.USER,
                        commandName: USER_COMMANDS.changePersonalSettings,
                        payload: personalSettingsChanges,
                    });
                    position = pos;
                }
            }

            return { position, userId: user?.id };
        },

        onError: (error) => {
            if (error instanceof Error) {
                toast.error(error?.message ?? t('TOP_HEADER.USER_SETTINGS.SETTINGS.ERROR_CHANGING_SETTINGS'), {
                    theme: 'colored',
                });
            }
        },

        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: queryKeys.getUser(user?.id) });
        },

        onSuccess: (data) => {
            if (data !== undefined && data.position !== undefined) {
                const { position, userId } = data;
                setQueryPosition(JSON.stringify(queryKeys.getUser(userId)), position);

                const SuccessMessage = () => {
                    const { t: translate } = useTranslation();

                    return <>{translate('TOP_HEADER.USER_SETTINGS.SETTINGS.SUCCESSFULLY_CHANGED_SETTINGS')}</>;
                };

                toast.success(<SuccessMessage />);
            }

            close();
        },
    });

    const onError: SubmitErrorHandler<UserSettingsPayload> = (errors) => {
        const ToastMessage = () => (
            <ul className="list-disc m-0 pl-5">
                {Object.entries(errors).map(([field, error]) => (
                    <li key={field}>
                        {field}: {error?.message || 'Invalid'}
                    </li>
                ))}
            </ul>
        );

        toast.error(<ToastMessage />, { theme: 'colored' });
    };

    const onSubmit: SubmitHandler<UserSettingsPayload> = (data) => {
        mutate(data);
    };

    return { onError, onSubmit };
}
